import Calculator from './Calculator'

export default class Commercial extends Calculator {
  getPercentageAsFraction (percentage) {
    return percentage / 100
  }

  // Interface properties/methods

  getTotalForAverage() {
    // In commercial developments, the total land area is used for the total (as the average is per unit area (i.e. sqft/sqm)).
    return this.getRawTotalUnitSize()
  }

  getRawTotalArea () {
    return this.getValue('netLandArea')
  }

  getRawTotalUnitSize () {
    return this.getValue('totalLandArea')
  }

  // Totals

  getGrossDevelopmentValue () {
    // We divide by the initial yield percentage to get the number which would be equal to
    // passingRent at initialYield% (a bit like reverse VAT calc).
    return this.getPassingRent() / this.getPercentageAsFraction(this.getValue('initialYield'))
  }

  getNetDevelopmentValue () {
    return (
      this.getGrossDevelopmentValue() -
      this.getPercentageOrFixedValue('purchasersCosts', this.getGrossDevelopmentValue())
    )
  }

  getBuildingCosts () {
    return (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getExternalsTotal() +
      this.getProfessionalFeesTotal() +
      this.getLettingAgentFeesTotal() +
      this.getLegalLettingFeesTotal() +
      // this.getSalesAgentFeesTotal() +
      // this.getLegalSalesFeesTotal() +
      this.getFinanceTotal()
    )
  }

  getProfitAndLandValueCosts () {
    return (
      this.getDeveloperProfitTotal() +
      this.getLegalSiteAcquisitionCostTotal() +
      this.getPlanningTotal() +
      this.getSurveysTotal()
    )
  }

  getLandValue () {
    return this.getNetDevelopmentValue() - this.getTotalCosts()
  }

  // 01 - GDV

  getPassingRent () {
    return this.getTotalUnitSize() * this.getValue('rentValue')
  }

  // 02 - Costs

  getBaseBuildCostTotal () {
    return this.getTotalForArea('baseBuildCost')
  }

  getDevelopmentContingencyTotal () {
    return this.getPercentageOrFixedValue('developmentContingency', this.getBaseBuildCostTotal())
  }

  getExternalsTotal () {
    return this.getPercentageOrFixedValue('externals', this.getBaseBuildCostTotal())
  }

  getProfessionalFeesTotal () {
    const costsSubTotal = (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getExternalsTotal()
    )

    return this.getPercentageOrFixedValue('professionalFees', costsSubTotal)
  }

  getLettingAgentFeesTotal () {
    return this.getPercentageOrFixedValue('lettingAgentFees', this.getPassingRent())
  }

  getLegalLettingFeesTotal () {
    return this.getPercentageOrFixedValue('legalLettingFees', this.getPassingRent())
  }

  getSalesAgentFeesTotal () {
    return this.getPercentageOrFixedValue('salesAgentFees', this.getGrossDevelopmentValue())
  }

  getLegalSalesFeesTotal () {
    return this.getPercentageOrFixedValue('legalSalesFees', this.getGrossDevelopmentValue())
  }

  getFinanceTotal () {
    const costsSubTotal = (
      this.getBaseBuildCostTotal() +
      this.getDevelopmentContingencyTotal() +
      this.getExternalsTotal() +
      this.getProfessionalFeesTotal() +
      this.getLettingAgentFeesTotal() +
      this.getLegalLettingFeesTotal()
    )

    return this.getPercentageOrFixedValue('finance', costsSubTotal)
  }

  // 03 - Profit

  getDeveloperProfitTotal () {
    let total

    if (this.data.profit.unit === 'percentgdv') {
      total =  this.getGrossDevelopmentValue()
    } else if (this.data.profit.unit === 'percentcost') {
      total = this.getBuildingCosts()
    }

    return this.getPercentageOrFixedValue('profit', total)
  }

  getLegalSiteAcquisitionCostTotal () {
    return this.getValue('legalSiteAcquisitionCost')
  }

  getPlanningTotal () {
    return this.getValue('planning')
  }

  getSurveysTotal () {
    return this.getValue('surveys')
  }

  // Translation

  translateToUnit (newUnit) {
    if (newUnit === 'imperial' && this.unit === 'metric') {
      return {
        ...this.data,
        unit: newUnit,

        totalLandArea: this.data.totalLandArea && this.hectaresToAcres(this.data.totalLandArea),
        rentValue: this.data.rentValue && this.psqmToPsqft(this.data.rentValue),
        baseBuildCost: this.data.baseBuildCost && this.psqmToPsqft(this.data.baseBuildCost),
      }
    } else if (newUnit === 'metric' && this.unit === 'imperial') {
      return {
        ...this.data,
        unit: newUnit,

        totalLandArea: this.data.totalLandArea && this.acresToHectares(this.data.totalLandArea),
        rentValue: this.data.rentValue && this.psqftToPsqm(this.data.rentValue),
        baseBuildCost: this.data.baseBuildCost && this.psqftToPsqm(this.data.baseBuildCost),
      }
    }

    return this.data
  }
}
